export type EnumValue = number;
export type EnumName = string;

export type EnumMember = {
	name: string;
	displayName: string;
	value: EnumValue;
};

export type SpreadEnum<TKeys extends string> = {
	[keys in TKeys]: EnumValue;
};

export type CustomEnumBase = {
	getDisplayNames(predicate?: () => boolean): string[];
	getByValue(value?: EnumValue): EnumMember | undefined;
	getByName(name?: EnumName): EnumMember | undefined;
	getDDLOptions({ defaultOption, predicate }): any[];
};

export type DDLOptions = {
	defaultOption?: any;
	predicate: (member: EnumMember) => boolean;
};

const defaultOptions: DDLOptions = {
	defaultOption: undefined,
	predicate: _member => true,
};

/**
 * Enum implementation to support dropdowns
 *
 * @example
 * export type AnimalKeys = "cat" | "dog";
 * export type AnimalWithKeys = CustomEnum & SpreadEnum<AnimalKeys>;
 *
 * export const Animals = new CustomEnum([
 *   { name: "cat", displayName: "Munchkin", value: 0 },
 *   { name: "dog" , displayName: "Labrador", value: 1 },
 * ]) as AnimalWithKeys;
 */
export class CustomEnum implements CustomEnumBase {
	items: EnumMember[];

	constructor(items: EnumMember[]) {
		this.items = items;

		for (const [key, value] of this.spreadItems()) {
			this[key] = value;
		}
	}

	spreadItems() {
		const dict: Record<string, EnumValue> = {};

		for (const i of this.items) {
			dict[i.name] = i.value;
		}

		return Object.entries(dict);
	}

	getDisplayNames(predicate = defaultOptions.predicate) {
		return this.items.filter(predicate).map(m => m.displayName);
	}

	getByValue(value?: EnumValue) {
		return this.items.find(({ value: v }) => v === value);
	}

	getByName(name?: EnumName) {
		return this.items.find(({ name: n }) => n === name);
	}

	getDisplayName(value?: EnumValue) {
		return this.getByValue(value)?.displayName || "";
	}

	getDDLOptions({ defaultOption, predicate } = defaultOptions) {
		let result = this.items;

		if (defaultOption) {
			result.unshift(defaultOption);
		}

		return result.filter(predicate);
	}
}
